'use client';

import * as Popover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import * as React from 'react';
import Icon from '../Icon';
import { IconNames } from '../Icon/iconMap';
import cn from '../Utils/cn';
import DefaultLabel from './DefaultLabel';
import InputMessage from './InputMessage';

type Value = {
	value: string;
	label: string;
};

type Props = {
	id?: string;
	name?: string;
	state?: string;
	value?: string | string[];
	placeholder?: string;
	searchPlaceholder?: string;
	label?: string;
	initialValue?: string | string[];
	required?: boolean;
	message?: string;
	disabled?: boolean;
	leftIcon?: IconNames;
	className?: string;
	showPills?: boolean;
	showArrow?: boolean;
	unstyled?: boolean;
	setValue?: (value: string | string[]) => void;
	setTouched?: (value: boolean) => void;
	labelProps?: Omit<React.ComponentPropsWithoutRef<typeof DefaultLabel>, 'children' | 'htmlFor'>;
	multiple?: boolean;
	inputProps?: {
		className?: string;
	};
	messageProps?: {
		className?: string;
	};
	options: {
		value: string;
		label: string;
		hideInSearch?: boolean;
	}[];
};

const SelectField = React.forwardRef<any, Props>(
	(
		{
			id,
			name,
			state,
			value,
			placeholder = 'Selecione...',
			label,
			initialValue = [],
			required = true,
			message,
			disabled = false,
			leftIcon = null,
			className,
			options,
			searchPlaceholder = 'Pesquisar...',
			labelProps,
			multiple = false,
			inputProps,
			messageProps,
			showPills = false,
			showArrow = true,
			unstyled = false,
			setValue = () => {},
		},
		ref,
	) => {
		const [open, setOpen] = React.useState(false);
		const [side, setSide] = React.useState<'top' | 'bottom'>('bottom');
		const [selected, setSelected] = React.useState<Value[]>(
			Array.isArray(initialValue)
				? options.filter(el => initialValue.includes(el.value))
				: options.filter(el => initialValue === el.value),
		);
		const contentRef = React.useRef<HTMLDivElement>(null);
		const MORef = React.useRef<MutationObserver | null>(null);
		const [search, setSearch] = React.useState('' as string);
		const genId = React.useId();
		const fieldId = id || name || genId;
		// O trigger é uma div[role=combobox], que <label htmlFor> não consegue rotular.
		// Ligamos pelo aria-labelledby para o nome acessível ser o label visível
		// ("Estado", "Cidade") em vez do placeholder genérico "Selecione...".
		const labelId = `${fieldId}-label`;

	React.useEffect(() => {
		if (disabled) setOpen(false);
	}, [disabled]);

	React.useEffect(() => {
		if (initialValue == null) return;
		if (!Array.isArray(initialValue) && initialValue == '') return;
		if (initialValue.length === 0) return;
		const initial = Array.isArray(initialValue) ? initialValue : [initialValue];
		setSelected(options.filter(el => initial.includes(el.value)));
	}, [initialValue]);

	// Sincroniza o selected com o value quando ele mudar
	React.useEffect(() => {
		if (value == null || value === '') {
			if (!multiple && selected.length > 0) {
				setSelected([]);
			}
			return;
		}
		if (multiple) {
			const valueArray = Array.isArray(value) ? value : [value];
			const newSelected = options.filter(el => valueArray.includes(el.value));
			if (JSON.stringify(newSelected.map(s => s.value).sort()) !== JSON.stringify(selected.map(s => s.value).sort())) {
				setSelected(newSelected);
			}
		} else {
			const valueStr = typeof value === 'string' ? value : '';
			const newSelected = options.filter(el => el.value === valueStr);
			if (newSelected.length > 0 && (!selected.length || selected[0]?.value !== valueStr)) {
				setSelected(newSelected);
			} else if (newSelected.length === 0 && selected.length > 0 && valueStr === '') {
				setSelected([]);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

		React.useEffect(() => {
			if (selected.some(el => !Boolean(el.value) && !Boolean(el.label)))
				setSelected(selected.filter(el => Boolean(el.value) && !Boolean(el.label)));
		}, [selected]);

		// Listen to collision changes and update the side
		React.useEffect(() => {
			MORef.current = new MutationObserver(() => {
				if (contentRef.current?.getAttribute('data-side') === 'bottom') {
					setSide('bottom');
				} else if (contentRef.current?.getAttribute('data-side') === 'top') {
					setSide('top');
				}
			});
		}, []);

		const filteredOptions = React.useMemo(() => {
			if (!search) return options;

			return options
				.filter(
					option =>
						!option.hideInSearch &&
						option.label !== 'Todos' &&
						option.label?.toLocaleLowerCase()?.startsWith(search?.toLocaleLowerCase()),
				)
				.sort((a, b) => a.label?.localeCompare(b.label));
		}, [search, options]);

		React.useImperativeHandle(ref, () => ({
			setSelectedValue: (val: string) => {
				setSelected([{ value: val, label: val }]);
			},
		}));

		return (
			<div
				className={cn(
					{
						'opacity-50': disabled,
					},
					className,
				)}
			>
				<DefaultLabel id={labelId} htmlFor={fieldId} required={required} {...labelProps}>
					{label}
				</DefaultLabel>

				<Popover.Root
					open={open}
					onOpenChange={value => {
						if (disabled) return;

						setOpen(value);
					}}
				>
					<Popover.Trigger asChild>
						<div
							id={fieldId}
							// eslint-disable-next-line jsx-a11y/role-has-required-aria-props
							role="combobox"
							aria-labelledby={label ? labelId : undefined}
							aria-label={label ? undefined : placeholder}
							aria-expanded={open}
							className={cn(
								'flex items-center min-h-[5rem] font-roboto text-lg leading-none font-normal bg-white w-full min-w-[270px] p-4 border border-solid border-base-grey-5 text-base-grey-5 cursor-pointer',
								{
									'rounded-2xl': !open,
									'rounded-t-2xl': open && side === 'bottom',
									'rounded-b-2xl': open && side === 'top',
									'cursor-default': disabled,
									'hover:border-1 hover:border-brand-main hover:px-4 focus:border-1 focus:border-brand-main focus:bg-brand-superlight focus:outline-none focus:px-4':
										!disabled && !unstyled,
									'border-1 border-error-dark bg-error-light px-4': state === 'error' && !disabled,
								},
								inputProps?.className,
							)}
						>
							{selected.length === 0 && leftIcon && <Icon name={leftIcon} className="ml-[0.4rem] mr-[1.6rem] text-base-grey-7" />}

							{showPills ? (
								<ul className="flex gap-2 flex-wrap text-base">
									{selected
										.sort((a, b) => {
											return (a.label?.length || 0) - (b.label?.length || 0);
										})
										.map(el => {
											return el.label != null ? (
												<li
													key={el.value}
													className="flex gap-2 p-3 rounded-2xl border border-solid border-brand-main bg-brand-main/10 text-brand-main"
												>
													{el.label}
													<button
														type="button"
														onClick={ev => {
															ev.stopPropagation();

															const newSelected = selected.filter(item => item.value !== el.value);

															setSelected(newSelected);
															setValue(newSelected.map(item => item.value));
														}}
													>
														<Icon name="close" className="text-base-grey-4 hover:text-base-grey-5" />
													</button>
												</li>
											) : null;
										})}
								</ul>
							) : (
								selected
									.map(el => el.label)
									.filter(o => Boolean(o))
									.join(', ')
							)}

							{selected.length === 0 && placeholder}

							{showArrow && (
								// Indicador visual (sem onClick próprio — o combobox inteiro é o
								// elemento interativo); fica fora da árvore de acessibilidade.
								<button type="button" aria-hidden="true" tabIndex={-1} className="ml-auto w-10 h-10 flex-shrink-0 flex items-center justify-center">
									<Icon name="down-angle" className="w-4 h-4" />
								</button>
							)}
						</div>
					</Popover.Trigger>
					<Popover.Content
						onOpenAutoFocus={() => {
							if (contentRef.current) {
								MORef.current?.observe(contentRef.current, {
									attributes: true,
								});
							}
						}}
						onCloseAutoFocus={() => {
							MORef.current?.disconnect();
						}}
						ref={contentRef}
						className="w-[var(--radix-popover-trigger-width)] p-0 border data-[side=bottom]:rounded-b-2xl data-[side=top]:rounded-t-2xl border-solid border-base-grey-5 bg-white body-3 body-base overflow-hidden z-50"
						align="center"
						side="bottom"
					>
						<Command label="Command Menu" className="border-none" shouldFilter={false}>
							<div className="border-b p-3 bg-brand-light" cmdk-input-wrapper="">
								<div className="rounded-xl bg-white flex items-center gap-4 px-4">
									<Icon className="shrink-0 text-xs" name="search" />
									<Command.Input
										className="flex h-11 w-full rounded-md bg-transparent outline-none placeholder:text-base-grey-4 disabled:cursor-not-allowed disabled:opacity-50"
										placeholder={searchPlaceholder}
										onValueChange={value => setSearch(value)}
										value={search}
									/>
								</div>
							</div>
							<Command.List className="max-h-80 overflow-y-auto">
								<Command.Empty className="cursor-default select-none items-center p-[1.6rem] py-3 text-base-grey-5">
									Sem resultados.
								</Command.Empty>

								{filteredOptions.map(option => (
									<Command.Item
										key={option.value}
										value={option.value || ''}
										className="flex gap-4 text-base-grey-5 cursor-pointer select-none items-center p-[1.6rem] py-5 outline-none aria-selected:bg-brand-superlight aria-selected:font-bold data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
										onSelect={() => {
											if (multiple) {
												setSelected(
													selected.find(el => el.value === option.value)
														? selected.filter(el => el.value !== option.value)
														: [...selected, option],
												);

												setValue(
													selected.find(el => el.value === option.value)
														? selected.filter(el => el.value !== option.value).map(el => el.value)
														: [...selected.map(el => el.value), option.value],
												);
											} else {
												setSelected(selected.find(el => el.value === option.value) ? [] : [option]);
												setValue(selected.find(el => el.value === option.value) ? '' : option.value);
											}

											if (!multiple) {
												setOpen(false);
											}
										}}
									>
										<Icon
											className={cn('text-xs', {
												'opacity-100': selected.find(el => el.value === option.value),
												'opacity-0': !selected.find(el => el.value === option.value),
											})}
											name="check"
										/>
										{option.label}
									</Command.Item>
								))}
							</Command.List>
						</Command>
					</Popover.Content>
				</Popover.Root>

				<InputMessage state={state} className={messageProps?.className}>
					{message}
				</InputMessage>
			</div>
		);
	},
);

SelectField.displayName = 'SelectField';

export default SelectField;
